import ReportsFilters from './ReportsFilters';
import KpiCards from './KpiCards';
import SimpleChart from './SimpleChart';
import ReportTable from './ReportTable';
import { formatCurrency, formatNumber } from './formatters';

const RevenueReports = ({
  data,
  filters,
  onFiltersChange,
  onApply,
  onExport,
  loading,
  exporting,
  properties = [],
  tours = [],
  hosts = [],
  showHost = false,
  showClient = false,
  loadingMeta = false,
}) => {
  const kpis = data?.kpis || {};
  const type = filters.type || 'all';
  const showPropertyBreakdown = type !== 'tour';
  const showTourBreakdown = type !== 'property';
  const showHostBreakdown = showHost && (data?.breakdownByHost?.length || 0) > 0;
  const showClientBreakdown = showClient && (data?.breakdownByClient?.length || 0) > 0;

  return (
    <div className="space-y-4">
      <ReportsFilters
        filters={filters}
        onChange={onFiltersChange}
        onApply={onApply}
        onExport={onExport}
        properties={properties}
        tours={tours}
        hosts={hosts}
        clients={showClient ? data?.clients || [] : []}
        showHost={showHost}
        showProperty
        showTour
        showClient={showClient}
        showType
        showGroupBy
        loadingMeta={loadingMeta}
        applying={loading}
        exporting={exporting}
      />

      <KpiCards
        items={[
          { label: 'Gross revenue', value: kpis.grossRevenue, render: (v) => formatCurrency(v) },
          { label: 'Host earnings', value: kpis.hostEarnings, render: (v) => formatCurrency(v) },
          { label: 'Platform fees', value: kpis.platformFees, render: (v) => formatCurrency(v) },
          {
            label: 'Bookings',
            value: kpis.totalBookings,
            render: (v) => formatNumber(v),
            hint: `${formatNumber(kpis.cancelledBookings)} cancelled`,
          },
          { label: 'Refunds', value: kpis.refundsTotal, render: (v) => formatCurrency(v) },
        ]}
      />

      <SimpleChart
        data={data?.timeseries || []}
        series={[
          { key: 'grossRevenue', label: 'Gross revenue', color: '#0ea5e9' },
          { key: 'hostEarnings', label: 'Host earnings', color: '#22c55e' },
        ]}
      />

      {showPropertyBreakdown && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-800">By property</h3>
          <ReportTable
            loading={loading}
            columns={[
              { key: 'title', label: 'Property' },
              { key: 'grossRevenue', label: 'Gross', render: (v) => formatCurrency(v) },
              { key: 'hostEarnings', label: 'Host earnings', render: (v) => formatCurrency(v) },
              { key: 'platformFees', label: 'Fees', render: (v) => formatCurrency(v) },
              { key: 'totalBookings', label: 'Bookings', render: formatNumber },
            ]}
            rows={data?.breakdownByProperty || []}
            emptyText="No property revenue yet."
          />
        </div>
      )}

      {showTourBreakdown && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-800">By tour</h3>
          <ReportTable
            loading={loading}
            columns={[
              { key: 'title', label: 'Tour' },
              { key: 'grossRevenue', label: 'Gross', render: (v) => formatCurrency(v) },
              { key: 'hostEarnings', label: 'Host earnings', render: (v) => formatCurrency(v) },
              { key: 'platformFees', label: 'Fees', render: (v) => formatCurrency(v) },
              { key: 'totalBookings', label: 'Bookings', render: formatNumber },
            ]}
            rows={data?.breakdownByTour || []}
            emptyText="No tour revenue yet."
          />
        </div>
      )}

      {showHostBreakdown && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-800">By host</h3>
          <ReportTable
            loading={loading}
            columns={[
              { key: 'hostName', label: 'Host' },
              { key: 'grossRevenue', label: 'Gross', render: (v) => formatCurrency(v) },
              { key: 'hostEarnings', label: 'Host earnings', render: (v) => formatCurrency(v) },
              { key: 'platformFees', label: 'Fees', render: (v) => formatCurrency(v) },
              { key: 'totalBookings', label: 'Bookings', render: formatNumber },
            ]}
            rows={data?.breakdownByHost || []}
            emptyText="No host revenue yet."
          />
        </div>
      )}

      {showClientBreakdown && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-800">By client</h3>
          <ReportTable
            loading={loading}
            columns={[
              { key: 'clientName', label: 'Client' },
              { key: 'grossRevenue', label: 'Gross', render: (v) => formatCurrency(v) },
              { key: 'hostEarnings', label: 'Host earnings', render: (v) => formatCurrency(v) },
              { key: 'platformFees', label: 'Fees', render: (v) => formatCurrency(v) },
              { key: 'totalBookings', label: 'Bookings', render: formatNumber },
            ]}
            rows={data?.breakdownByClient || []}
            emptyText="No client revenue yet."
          />
        </div>
      )}
    </div>
  );
};

export default RevenueReports;
